import { useCallback } from 'react';

const STORAGE_KEY = 'quotations-state-snapshot';
const SCROLL_CONTAINER_ID = 'dashboard-scroll-container';

export const QUOTATIONS_SNAPSHOT_DEFAULTS = {
  activeTab: 'templates',
  orders: {
    search: '',
    status: '',
    quotationId: '',
    page: 1,
    scrollTop: 0,
  },
  templates: {
    search: '',
    page: 1,
    scrollTop: 0,
  },
};

export function readQuotationsSnapshot() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...QUOTATIONS_SNAPSHOT_DEFAULTS };
    const parsed = JSON.parse(raw);
    return {
      ...QUOTATIONS_SNAPSHOT_DEFAULTS,
      ...parsed,
      orders: {
        ...QUOTATIONS_SNAPSHOT_DEFAULTS.orders,
        ...(parsed.orders || {}),
      },
      templates: {
        ...QUOTATIONS_SNAPSHOT_DEFAULTS.templates,
        ...(parsed.templates || {}),
      },
    };
  } catch {
    return { ...QUOTATIONS_SNAPSHOT_DEFAULTS };
  }
}

export function writeQuotationsSnapshot(next) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private-mode errors
  }
}

export function clearQuotationsPersistence() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function useQuotationsStateSnapshot() {
  const saveSnapshot = useCallback((partial) => {
    if (!partial || typeof partial !== 'object') return;
    const current = readQuotationsSnapshot();
    writeQuotationsSnapshot({
      ...current,
      ...partial,
    });
  }, []);

  const saveOrdersState = useCallback((partial) => {
    if (!partial || typeof partial !== 'object') return;
    const current = readQuotationsSnapshot();
    writeQuotationsSnapshot({
      ...current,
      orders: {
        ...current.orders,
        ...partial,
      },
    });
  }, []);

  const saveTemplatesState = useCallback((partial) => {
    if (!partial || typeof partial !== 'object') return;
    const current = readQuotationsSnapshot();
    writeQuotationsSnapshot({
      ...current,
      templates: {
        ...current.templates,
        ...partial,
      },
    });
  }, []);

  const attachScrollListener = useCallback(
    (tab = 'orders') => {
      const el = document.getElementById(SCROLL_CONTAINER_ID);
      if (!el) return () => {};

      let rafId = null;
      const onScroll = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          if (tab === 'orders') {
            saveOrdersState({ scrollTop: el.scrollTop });
          } else {
            saveTemplatesState({ scrollTop: el.scrollTop });
          }
        });
      };

      el.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        el.removeEventListener('scroll', onScroll);
        if (rafId) cancelAnimationFrame(rafId);
      };
    },
    [saveOrdersState, saveTemplatesState],
  );

  const restoreScroll = useCallback((tab = 'orders') => {
    const el = document.getElementById(SCROLL_CONTAINER_ID);
    if (!el) return;
    const snapshot = readQuotationsSnapshot();
    const targetTop =
      tab === 'orders'
        ? snapshot.orders?.scrollTop || 0
        : snapshot.templates?.scrollTop || 0;

    if (targetTop > 0) {
      el.scrollTop = targetTop;
      requestAnimationFrame(() => {
        if (el) el.scrollTop = targetTop;
      });
      setTimeout(() => {
        if (el) el.scrollTop = targetTop;
      }, 50);
    }
  }, []);

  return {
    readSnapshot: readQuotationsSnapshot,
    saveSnapshot,
    saveOrdersState,
    saveTemplatesState,
    attachScrollListener,
    restoreScroll,
  };
}
